// Injects the PDF top-card (title, QR code, exported-by) and info-header row
// (avatar, source name, date, read time, topic pills) into the print iframe's
// document, plus all PDF-specific CSS overrides.
//
// The header is built with createElement/textContent so user-controlled fields
// (title, source name, channels, exporter) are inserted as inert text by the
// DOM — there is no HTML string concatenation and nothing needs pre-escaping.
// The QR SVG is the single intentional raw-markup insertion (see below).
import { buildPrintStyles } from './print-styles';
import { PdfHeaderData } from './types';

// The avatar color is the one value placed into a CSS declaration, so restrict
// it to safe CSS-color characters before use.
const safeCssColor = (s: string): string =>
  s.replace(/[^a-zA-Z0-9#%(),. ]/g, '');

// Only http(s) avatar URLs reach the <img src>; anything else (javascript:,
// data:, …) is dropped and the colored-initial fallback renders instead.
const safeHttpUrl = (s: string): string => (/^https?:\/\//i.test(s) ? s : '');

// Create an element with an optional class and text. textContent is escaped by
// the DOM, so any string passed here is rendered as literal text, never markup.
const el = (
  doc: Document,
  tag: string,
  className?: string,
  text?: string
): HTMLElement => {
  const node = doc.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
};

// Top metadata card: title + "Exported by …" byline on the left, QR card right.
const buildTopCard = (doc: Document, data: PdfHeaderData): HTMLElement => {
  const { title, exportedBy, exportedAtStr, qrSvg } = data;
  const topCard = el(doc, 'div', 'pdf-top');

  const meta = el(doc, 'div', 'pdf-top-meta');
  if (title) meta.appendChild(el(doc, 'h1', 'pdf-top-title', title));
  meta.appendChild(
    el(
      doc,
      'p',
      'pdf-top-byline',
      `Exported by ${exportedBy} on ${exportedAtStr}`
    )
  );
  topCard.appendChild(meta);

  const qrWrap = el(doc, 'div', 'pdf-top-qr');
  const qrCard = el(doc, 'div', 'pdf-top-qr-card');
  // "Scan me!" — the leading S gets tighter spacing to match the design.
  const qrLabel = el(doc, 'span', 'pdf-top-qr-label');
  const sSpan = el(doc, 'span', undefined, 'S');
  sSpan.style.letterSpacing = '-0.5px';
  qrLabel.appendChild(sSpan);
  qrLabel.appendChild(doc.createTextNode('can me!'));
  qrCard.appendChild(qrLabel);
  const qrFrame = el(doc, 'div', 'pdf-top-qr-frame');
  // The ONLY intentional raw-HTML insertion: trusted SVG markup produced by
  // generateQrSvg(). Never assign caller-controlled text here.
  qrFrame.innerHTML = qrSvg;
  qrCard.appendChild(qrFrame);
  qrWrap.appendChild(qrCard);
  topCard.appendChild(qrWrap);

  return topCard;
};

// Info-header row: avatar (or colored-initial fallback), source name, published
// date, read time, then topic pills aligned to the right.
const buildInfoHeader = (doc: Document, data: PdfHeaderData): HTMLElement => {
  const {
    sourceName,
    sourceAvatarUrl,
    sourceAvatarColor,
    publishedAtStr,
    readTimeStr,
    channels,
  } = data;
  const infoHeader = el(doc, 'div', 'pdf-info-header');
  const sourceRow = el(doc, 'div', 'pdf-info-source');

  const avatarUrl = safeHttpUrl(sourceAvatarUrl);
  if (avatarUrl) {
    const img = doc.createElement('img');
    img.className = 'pdf-info-avatar';
    img.setAttribute('src', avatarUrl);
    img.setAttribute('alt', '');
    sourceRow.appendChild(img);
  } else {
    const fallback = el(
      doc,
      'div',
      'pdf-info-avatar-fallback',
      sourceName ? sourceName[0].toUpperCase() : '?'
    );
    fallback.style.background = safeCssColor(sourceAvatarColor);
    sourceRow.appendChild(fallback);
  }

  if (sourceName)
    sourceRow.appendChild(el(doc, 'span', 'pdf-info-source-name', sourceName));
  if (publishedAtStr) {
    sourceRow.appendChild(el(doc, 'span', 'pdf-info-dot', '·'));
    sourceRow.appendChild(el(doc, 'span', undefined, publishedAtStr));
  }
  if (readTimeStr) {
    sourceRow.appendChild(el(doc, 'span', 'pdf-info-dot', '·'));
    sourceRow.appendChild(el(doc, 'span', undefined, readTimeStr));
  }
  infoHeader.appendChild(sourceRow);

  if (channels.length > 0) {
    const tags = el(doc, 'div', 'pdf-info-channels');
    channels.forEach((c) =>
      tags.appendChild(el(doc, 'span', 'pdf-info-tag', c))
    );
    infoHeader.appendChild(tags);
  }

  return infoHeader;
};

// Neutralize any fixed/sticky article elements — in the print renderer these
// are placed at their viewport-relative position (top of page), visually
// overlaying our injected header and creating apparent blank space.
const neutralizeFixedElements = (doc: Document, win: Window): void => {
  Array.from(doc.querySelectorAll<HTMLElement>('*')).forEach((node) => {
    const pos = win.getComputedStyle(node).position;
    if (pos === 'fixed' || pos === 'sticky') {
      node.style.setProperty('position', 'static', 'important');
    }
  });
};

// Move the existing article body into a padded wrapper (narrower than the
// header) and zero the top spacing on its first element so email-style table
// wrappers / spacer cells don't push content away from the header.
const wrapArticleBody = (doc: Document, win: Window | null): HTMLElement => {
  const articleWrapper = doc.createElement('div');
  articleWrapper.style.cssText = 'padding-left: 15px; padding-right: 15px;';
  while (doc.body.firstChild) {
    articleWrapper.appendChild(doc.body.firstChild);
  }
  doc.body.appendChild(articleWrapper);

  const firstArticle = articleWrapper.firstChild;
  if (firstArticle instanceof HTMLElement) {
    firstArticle.style.setProperty('margin-top', '0', 'important');
    firstArticle.style.setProperty('padding-top', '0', 'important');
    // Recurse one level into a table wrapper's first cell (common email pattern)
    const firstCell = firstArticle.querySelector<HTMLElement>('td, th');
    if (firstCell && win) {
      const cs = win.getComputedStyle(firstCell);
      if (
        !firstCell.textContent?.trim() ||
        cs.height === '0px' ||
        parseInt(cs.height, 10) < 4
      ) {
        // Looks like a spacer cell — collapse it
        firstCell.style.setProperty('height', '0', 'important');
        firstCell.style.setProperty('padding', '0', 'important');
        firstCell.style.setProperty('font-size', '0', 'important');
        firstCell.style.setProperty('line-height', '0', 'important');
      } else {
        firstCell.style.setProperty('padding-top', '0', 'important');
      }
    }
  }
  return articleWrapper;
};

export function injectPdfHeader(doc: Document, data: PdfHeaderData): void {
  const win = doc.defaultView;

  const style = doc.createElement('style');
  style.dataset.pdfInjected = '1';
  style.textContent = buildPrintStyles(data.robotoFontUrl);
  doc.head.appendChild(style);

  const topCard = buildTopCard(doc, data);
  const infoHeader = buildInfoHeader(doc, data);

  if (win) neutralizeFixedElements(doc, win);

  const articleWrapper = wrapArticleBody(doc, win);
  doc.body.insertBefore(infoHeader, articleWrapper);
  doc.body.insertBefore(topCard, infoHeader);
}
